import { FRACTAL_COLOR_ROLES, FRACTAL_PALETTES, randomFractalColors } from "../lib/fractal-palettes";
import { Card, CardContent } from "@/components/ui/card";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useDominantColors } from "@/hooks/useColor";
import { useCurrentTrack } from "@/hooks/useCurrentTrack";
import { useSpotifyAuth } from "@/hooks/useSpotifyAuth";
import { cn } from "@/lib/utils";
import { Gauge, Link2, MonitorUp, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import FullscreenIcon from "../assets/fullscreen.svg?react";
import RandomIcon from "../assets/random.svg?react";
import SettingsIcon from "../assets/settings.svg?react";
import { getPresetsForMode, type AppPreset } from "../lib/app-presets";
import { DEFAULT_BLISTER_SETTINGS, normalizeBlisterSettings, randomizeBlisterSettings } from "../lib/blister-settings";
import { DEFAULT_FRACTAL_SETTINGS, normalizeFractalSettings, randomizeFractalSettings } from "../lib/fractal-settings";
import {
  DEFAULT_KALEIDOSCOPE_SETTINGS,
  normalizeKaleidoscopeSettings,
  randomizeKaleidoscopeSettings,
} from "../lib/kaleidoscope-settings";
import { PERFORMANCE_TIERS, type PerformanceTier } from "../lib/performance-profile";
import type { ViewMode } from "../lib/shareable-settings";
import type { FractalSettings } from "../types/fractal";
import type { KaleidoscopeSettings } from "../types/kaleidoscope";
import type { BlisterSettings } from "../types/settings";
import { BlisterSettingsPanel } from "./BlisterSettings";
import { KaleidoscopeSettingsPanel } from "./KaleidoscopeSettings";
import { SpotifyPanel } from "./Spotify";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";

export const DEFAULT_SETTINGS: BlisterSettings = DEFAULT_BLISTER_SETTINGS;

type QuickControlKey = "energy" | "softness" | "motion" | "glow" | "depth";
type QuickValues = Record<QuickControlKey, number>;

function clamp(value: number, min = 0, max = 1) {
  return Math.min(max, Math.max(min, value));
}

function formatPercent(value: number) {
  return `${Math.round(value * 100)}%`;
}

const VOXEL_MATERIAL_OPTIONS = [
  { value: 0, label: "Matte" },
  { value: 1, label: "Metal" },
  { value: 2, label: "Glass" },
] as const;

function QuickSlider({
  label,
  value,
  onPreview,
  onCommit,
}: {
  label: string;
  value: number;
  onPreview: (value: number) => void;
  onCommit: (value: number) => void;
}) {
  const [draftValue, setDraftValue] = useState(value);
  const draggingRef = useRef(false);
  const draftValueRef = useRef(value);

  useEffect(() => {
    if (!draggingRef.current) {
      setDraftValue(value);
      draftValueRef.current = value;
    }
  }, [value]);

  const commitDraft = () => {
    draggingRef.current = false;
    const next = clamp(draftValueRef.current);
    setDraftValue(next);
    onCommit(next);
  };

  return (
    <label className="block">
      <div className="mb-1.5 flex items-center justify-between gap-2 text-xs font-medium text-white/82">
        <span>{label}</span>
        <span className="text-white/48">{formatPercent(draftValue)}</span>
      </div>
      <input
        type="range"
        min={0}
        max={1}
        step={0.01}
        value={draftValue}
        onPointerDown={() => {
          draggingRef.current = true;
          onPreview(draftValueRef.current);
        }}
        onChange={(event) => {
          draggingRef.current = true;
          const next = Number(event.target.value);
          draftValueRef.current = next;
          setDraftValue(next);
          onPreview(next);
        }}
        onPointerUp={commitDraft}
        onTouchEnd={commitDraft}
        onMouseUp={commitDraft}
        onBlur={commitDraft}
        onKeyUp={(event) => {
          if (event.key === "Enter" || event.key === " " || event.key.startsWith("Arrow")) {
            commitDraft();
          }
        }}
        className="h-2 w-full cursor-pointer accent-white"
      />
    </label>
  );
}

function PresetButton({
  preset,
  active,
  onClick,
}: {
  preset: AppPreset;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`min-w-0 flex-1 rounded-lg border p-3 text-left transition hover:bg-white/[0.1] ${active
        ? "border-white/35 bg-white/[0.14]"
        : "border-white/10 bg-white/[0.05]"
        }`}
    >
      <div className="mb-2 flex gap-1">
        {preset.colors.slice(0, 5).map((color) => (
          <span
            key={color}
            className="h-3 flex-1 rounded-full border border-white/10"
            style={{ backgroundColor: color }}
          />
        ))}
      </div>
      <div className="text-sm font-semibold text-white">{preset.name}</div>
      <div className="mt-1 text-xs leading-snug text-white/58">{preset.description}</div>
    </button>
  );
}

function useIsMobileViewport() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false
  );

  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(media.matches);

    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return isMobile;
}

export function SettingsPanel({
  viewMode,
  blisterSettings,
  onBlisterChange,
  fractalSettings,
  onFractalChange,
  kaleidoscopeSettings,
  onKaleidoscopeChange,
  onCopyShareHash,
  onApplyShareHash,
  shareHashCopied = false,
  performanceTier,
  onPerformanceTierChange,
  screenWakeLockEnabled,
  onScreenWakeLockChange,
  screenWakeLockSupported,
  screenWakeLockActive,
  uiVisible = true,
}: {
  viewMode: ViewMode;
  blisterSettings: BlisterSettings;
  onBlisterChange: (settings: BlisterSettings) => void;
  fractalSettings: FractalSettings;
  onFractalChange: (settings: FractalSettings) => void;
  kaleidoscopeSettings: KaleidoscopeSettings;
  onKaleidoscopeChange: (settings: KaleidoscopeSettings) => void;
  onCopyShareHash?: () => void;
  onApplyShareHash?: (value: string) => boolean;
  shareHashCopied?: boolean;
  performanceTier: PerformanceTier;
  onPerformanceTierChange: (tier: PerformanceTier) => void;
  screenWakeLockEnabled: boolean;
  onScreenWakeLockChange: (enabled: boolean) => void;
  screenWakeLockSupported: boolean;
  screenWakeLockActive: boolean;
  uiVisible?: boolean;
}) {
  const { token, login, logout } = useSpotifyAuth();
  const track = useCurrentTrack(token);
  const isMobileViewport = useIsMobileViewport();

  // Get album cover url
  const albumImgUrl =
    track &&
      track.item &&
      track.item.album &&
      track.item.album.images &&
      track.item.album.images[2]?.url
      ? track.item.album.images[2].url
      : null;

  // Get colors from album cover
  const dominantColors = useDominantColors(albumImgUrl, 6);

  // Update only colors in settings if dominantColors changed and are different from current
  useEffect(() => {
    if (dominantColors && dominantColors.length > 0) {
      if (viewMode === "kaleidoscope" &&
        JSON.stringify(kaleidoscopeSettings.colors) !== JSON.stringify(dominantColors)) {
        onKaleidoscopeChange({
          ...kaleidoscopeSettings,
          colors: dominantColors,
        });
      } else if (viewMode === "glitter" &&
        JSON.stringify(blisterSettings.colors) !== JSON.stringify(dominantColors)) {
        onBlisterChange({
          ...blisterSettings,
          colors: dominantColors,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dominantColors, viewMode]);

  // Logout if token was removed from localStorage (e.g. on 401)
  useEffect(() => {
    if (token && !localStorage.getItem("spotify_token")) {
      logout();
    }
  }, [token, track, logout]);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const controlsVisible = uiVisible || settingsOpen;
  const [fullscreen, setFullscreen] = useState(
    !!(typeof document !== "undefined" && document.fullscreenElement)
  );
  const [lastTrackId, setLastTrackId] = useState<string | null>(null);
  const [shareHashInput, setShareHashInput] = useState("");
  const [activePresetId, setActivePresetId] = useState<string | null>(null);
  const [quickPreviewValues, setQuickPreviewValues] = useState<QuickValues | null>(null);
  const isFractalMode = viewMode === "fractal";
  const fractalPresetOnly = false;

  const settingsRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close settings when clicking outside
  useEffect(() => {
    if (isMobileViewport) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        settingsOpen &&
        settingsRef.current &&
        buttonRef.current &&
        !settingsRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setSettingsOpen(false);
      }
    };

    if (settingsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileViewport, settingsOpen]);

  // Automatically lock colors if Spotify is active
  useEffect(() => {
    if (track && track.item && track.item.id && track.item.id !== lastTrackId) {
      toast(`${track.item.name}`, {
        className: "flex justify-between border border-white/10 bg-white/[0.1] text-white backdrop-blur-xl",
        style: {
          background: "rgba(255, 255, 255, 0.1)",
          borderColor: "rgba(255, 255, 255, 0.12)",
          color: "rgba(255, 255, 255, 0.96)",
        },
        description: `${track.item.artists.map((a: any) => a.name).join(", ")}`,
        action: (
          <img
            src={track.item.album.images[2]?.url}
            alt="track cover"
            className="rounded size-16"
          />
        ),
      });
      setLastTrackId(track.item.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track]);

  // Fullscreen logic
  const handleFullscreen = () => {
    const elem = document.documentElement;
    const isFs = document.fullscreenElement;
    if (!isFs) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if ((elem as any).webkitRequestFullscreen) {
        (elem as any).webkitRequestFullscreen();
      } else if ((elem as any).msRequestFullscreen) {
        (elem as any).msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen();
      }
    }
  };

  useEffect(() => {
    const onFullscreenChange = () => {
      const isFs = document.fullscreenElement;
      setFullscreen(!!isFs);
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    document.addEventListener("webkitfullscreenchange", onFullscreenChange);
    document.addEventListener("msfullscreenchange", onFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        onFullscreenChange
      );
      document.removeEventListener("msfullscreenchange", onFullscreenChange);
    };
  }, []);

  const handleSpotifyColors = () => {
    if (dominantColors && dominantColors.length > 0) {
      if (viewMode === "kaleidoscope") {
        onKaleidoscopeChange({ ...kaleidoscopeSettings, colors: dominantColors });
      } else if (viewMode === "glitter") {
        onBlisterChange({ ...blisterSettings, colors: dominantColors });
      } else if (viewMode === "fractal") {
        onFractalChange(normalizeFractalSettings({ ...fractalSettings, colors: dominantColors }));
      }
    } else if (viewMode === "fractal") {
      toast("No Spotify track colors yet");
    }
  };

  const handleRandomFractalColors = () => {
    setActivePresetId(null);
    onFractalChange(normalizeFractalSettings({ ...fractalSettings, colors: randomFractalColors(fractalSettings.colors) }));
  };

  const handleApplyShareHash = () => {
    if (!onApplyShareHash) {
      return;
    }

    const applied = onApplyShareHash(shareHashInput);
    if (applied) {
      setShareHashInput("");
    }
  };

  const currentPresets = getPresetsForMode(viewMode);

  const applyPreset = (preset: AppPreset) => {
    setActivePresetId(preset.id);
    setQuickPreviewValues(null);

    if (preset.mode === "kaleidoscope") {
      onKaleidoscopeChange(preset.settings as KaleidoscopeSettings);
    } else if (preset.mode === "fractal") {
      onFractalChange(
        normalizeFractalSettings({
          ...(preset.settings as FractalSettings),
          motionSpeed: fractalSettings.motionSpeed,
        })
      );
    } else {
      onBlisterChange(preset.settings as BlisterSettings);
    }
  };

  const getQuickValuesForSettings = ({
    blister = blisterSettings,
    fractal = fractalSettings,
    kaleidoscope = kaleidoscopeSettings,
  }: {
    blister?: BlisterSettings;
    fractal?: FractalSettings;
    kaleidoscope?: KaleidoscopeSettings;
  } = {}): QuickValues => {
    if (viewMode === "kaleidoscope") {
      const settings = kaleidoscope;
      return {
        energy: clamp((
          (settings.highlightIntensity - 0.24) / 0.96 +
          (settings.paletteIntensity - 0.72) / 0.56 +
          (settings.contrast - 0.42) / 0.58
        ) / 3),
        softness: clamp((
          (1.08 - settings.contrast) / 0.58 +
          (settings.bloomRadius - 0.12) / 0.46 +
          (0.52 - settings.textureAmount) / 0.28
        ) / 3),
        motion: clamp((
          settings.rotationSpeed / 0.42 +
          (settings.morphSpeed - 0.05) / 0.56 +
          (settings.shimmerSpeed - 0.08) / 0.82
        ) / 3),
        glow: clamp((
          (settings.glowStrength - 0.28) / 0.96 +
          (settings.highlightIntensity - 0.28) / 0.9 +
          (settings.bloomRadius - 0.1) / 0.42
        ) / 3),
        depth: clamp((
          (settings.layerDepth - 0.28) / 0.76 +
          (settings.outerReflection - 0.18) / 0.88 +
          (settings.centerStructure - 0.2) / 0.82
        ) / 3),
      };
    }

    if (viewMode === "fractal") {
      const settings = fractal;
      return {
        energy: clamp((
          (settings.intensity - 0.48) / 0.42 +
          (settings.contrast - 0.42) / 0.54 +
          (settings.paletteIntensity - 0.84) / 0.32
        ) / 3),
        softness: clamp((
          (0.9 - settings.contrast) / 0.34 +
          (settings.glow - 0.14) / 0.34 +
          (0.5 - settings.vignette) / 0.18
        ) / 3),
        motion: clamp((
          (settings.motionSpeed - 0.05) / 0.42 +
          settings.rotationSpeed / 0.2 +
          (settings.drift - 0.03) / 0.36 +
          (settings.breathing - 0.06) / 0.3
        ) / 4),
        glow: clamp((settings.glow - 0.08) / 0.54),
        depth: clamp((
          (settings.fractalDepth - 0.42) / 0.42 +
          (settings.foldStrength - 0.32) / 0.52 +
          (settings.detail - 0.46) / 0.4
        ) / 3),
      };
    }

    const settings = blister;
    return {
      energy: clamp((
        (settings.count - 650) / 3800 +
        (settings.sparkleCount - 220) / 1380 +
        (settings.opacity - 0.5) / 0.42
      ) / 3),
      softness: clamp((
        (settings.blurStrength - 1.5) / 12 +
        (settings.hazeAmount - 0.08) / 0.82 +
        (settings.depthSoftness - 0.18) / 0.78
      ) / 3),
      motion: clamp((
        (settings.speed - 0.025) / 0.15 +
        settings.windStrength / 1.35 +
        settings.pulseIntensity / 0.38
      ) / 3),
      glow: clamp((
        (settings.glowStrength - 2) / 13 +
        (settings.bloomIntensity - 0.18) / 1.28 +
        (settings.bloomRadius - 0.22) / 0.56
      ) / 3),
      depth: clamp((
        (settings.atmosphereIntensity - 0.18) / 0.9 +
        (settings.fieldDistortion - 0.05) / 0.88 +
        (settings.depthSoftness - 0.22) / 0.74
      ) / 3),
    };
  };

  const quickValues = quickPreviewValues ?? getQuickValuesForSettings();

  const projectQuickControl = (key: QuickControlKey, value: number) => {
    const next = clamp(value);

    if (viewMode === "kaleidoscope") {
      return getQuickValuesForSettings({
        kaleidoscope: normalizeKaleidoscopeSettings(buildKaleidoscopeQuickSettings(key, next)),
      });
    }

    if (viewMode === "fractal") {
      return getQuickValuesForSettings({
        fractal: normalizeFractalSettings(buildFractalQuickSettings(key, next)),
      });
    }

    return getQuickValuesForSettings({
      blister: normalizeBlisterSettings(buildBlisterQuickSettings(key, next)),
    });
  };

  const previewQuickControl = (key: QuickControlKey, value: number) => {
    setQuickPreviewValues(projectQuickControl(key, value));
  };

  function buildKaleidoscopeQuickSettings(key: QuickControlKey, next: number): KaleidoscopeSettings {
    return {
      ...kaleidoscopeSettings,
      ...(key === "energy"
        ? {
          highlightIntensity: 0.24 + next * 0.96,
          paletteIntensity: 0.72 + next * 0.56,
          contrast: 0.42 + next * 0.58,
        }
        : {}),
      ...(key === "softness"
        ? {
          contrast: 1.08 - next * 0.58,
          bloomRadius: 0.12 + next * 0.46,
          textureAmount: 0.52 - next * 0.28,
        }
        : {}),
      ...(key === "motion"
        ? {
          rotationSpeed: next * 0.42,
          morphSpeed: 0.05 + next * 0.56,
          shimmerSpeed: 0.08 + next * 0.82,
        }
        : {}),
      ...(key === "glow"
        ? {
          glowStrength: 0.28 + next * 0.96,
          highlightIntensity: 0.28 + next * 0.9,
          bloomRadius: 0.1 + next * 0.42,
        }
        : {}),
      ...(key === "depth"
        ? {
          layerDepth: 0.28 + next * 0.76,
          outerReflection: 0.18 + next * 0.88,
          centerStructure: 0.2 + next * 0.82,
        }
        : {}),
    };
  }

  function buildFractalQuickSettings(key: QuickControlKey, next: number): FractalSettings {
    return {
      ...fractalSettings,
      ...(key === "energy"
        ? {
          intensity: 0.48 + next * 0.42,
          contrast: 0.42 + next * 0.54,
          paletteIntensity: 0.84 + next * 0.32,
        }
        : {}),
      ...(key === "softness"
        ? {
          contrast: 0.9 - next * 0.34,
          glow: 0.14 + next * 0.34,
          vignette: 0.5 - next * 0.18,
        }
        : {}),
      ...(key === "motion"
        ? {
          motionSpeed: 0.05 + next * 0.42,
          rotationSpeed: next * 0.2,
          drift: 0.03 + next * 0.36,
          breathing: 0.06 + next * 0.3,
        }
        : {}),
      ...(key === "glow" ? { glow: 0.08 + next * 0.54, brightness: 0.36 + next * 0.22 } : {}),
      ...(key === "depth"
        ? {
          fractalDepth: 0.42 + next * 0.42,
          foldStrength: 0.32 + next * 0.52,
          detail: 0.46 + next * 0.4,
        }
        : {}),
    };
  }

  function buildBlisterQuickSettings(key: QuickControlKey, next: number): BlisterSettings {
    return {
      ...blisterSettings,
      ...(key === "energy"
        ? {
          count: Math.round(650 + next * 3800),
          sparkleCount: Math.round(220 + next * 1380),
          opacity: 0.5 + next * 0.42,
        }
        : {}),
      ...(key === "softness"
        ? {
          blurStrength: 1.5 + next * 12,
          hazeAmount: 0.08 + next * 0.82,
          depthSoftness: 0.18 + next * 0.78,
        }
        : {}),
      ...(key === "motion"
        ? {
          speed: 0.025 + next * 0.15,
          windStrength: next * 1.35,
          pulseIntensity: next * 0.38,
        }
        : {}),
      ...(key === "glow"
        ? {
          glowStrength: 2 + next * 13,
          bloomIntensity: 0.18 + next * 1.28,
          bloomRadius: 0.22 + next * 0.56,
        }
        : {}),
      ...(key === "depth"
        ? {
          atmosphereIntensity: 0.18 + next * 0.9,
          fieldDistortion: 0.05 + next * 0.88,
          depthSoftness: 0.22 + next * 0.74,
        }
        : {}),
    };
  };

  const applyQuickControl = (key: QuickControlKey, value: number) => {
    setActivePresetId(null);
    setQuickPreviewValues(null);
    const next = clamp(value);

    if (viewMode === "kaleidoscope") {
      onKaleidoscopeChange(normalizeKaleidoscopeSettings(buildKaleidoscopeQuickSettings(key, next)));
      return;
    }

    if (viewMode === "fractal") {
      onFractalChange(normalizeFractalSettings(buildFractalQuickSettings(key, next)));
      return;
    }

    onBlisterChange(normalizeBlisterSettings(buildBlisterQuickSettings(key, next)));
  };

  const renderPanelContent = (className?: string) => (
    <div className={cn("flex min-h-0 flex-col gap-4 custom-scrollbar", className)}>
      <div className="flex flex-wrap gap-2">
        {!isFractalMode ? (
          <>
            <Button
              variant="outline"
              className="cursor-pointer border-white/15 bg-white/[0.05] px-2.5 py-1 text-[12px] text-white hover:bg-white/[0.1]"
              onClick={() => {
                setActivePresetId(null);
                setQuickPreviewValues(null);
                if (viewMode === "kaleidoscope") {
                  onKaleidoscopeChange(DEFAULT_KALEIDOSCOPE_SETTINGS);
                } else if (viewMode === "fractal") {
                  onFractalChange(DEFAULT_FRACTAL_SETTINGS);
                } else if (viewMode === "glitter") {
                  onBlisterChange(DEFAULT_BLISTER_SETTINGS);
                }
              }}
            >
              Reset
            </Button>
            <Button
              variant="outline"
              className="flex cursor-pointer items-center justify-center border-white/15 bg-white/[0.05] px-2.5 py-1 hover:bg-white/[0.1]"
              onClick={() => {
                setActivePresetId(null);
                setQuickPreviewValues(null);
                if (viewMode === "kaleidoscope") {
                  onKaleidoscopeChange(randomizeKaleidoscopeSettings());
                } else if (viewMode === "fractal") {
                  onFractalChange(randomizeFractalSettings());
                } else if (viewMode === "glitter") {
                  onBlisterChange(randomizeBlisterSettings());
                }
              }}
              title="Random settings"
            >
              <RandomIcon className="size-5" />
            </Button>
          </>
        ) : null}
        {onCopyShareHash ? (
          <Button
            variant="outline"
            className="cursor-pointer gap-2 border-white/15 bg-white/[0.05] px-2.5 py-1 text-[12px] hover:bg-white/[0.1]"
            onClick={onCopyShareHash}
            title="Copy shareable URL hash"
          >
            <Link2 className="size-4" />
            {shareHashCopied ? "Copied" : "Copy hash"}
          </Button>
        ) : null}
      </div>

      <section className="rounded-xl border border-white/10 bg-black/20 p-3">
        <div className="mb-3 flex items-start gap-2">
          <Sparkles className="mt-0.5 size-4 text-white/70" />
          <div>
            <h4 className="text-sm font-semibold text-white">Presets</h4>
            <p className="mt-1 text-xs text-white/58">
              {isFractalMode ? "Choose a reference shader." : "Start from a curated scene, then tune the feel."}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 pb-1">
          {currentPresets.map((preset) => (
            <PresetButton
              key={preset.id}
              preset={preset}
              active={
                activePresetId === preset.id ||
                (isFractalMode &&
                  preset.mode === "fractal" &&
                  (preset.settings as FractalSettings).fractalVariant === fractalSettings.fractalVariant)
              }
              onClick={() => applyPreset(preset)}
            />
          ))}
        </div>
      </section>

      {isFractalMode ? (
      <section className="rounded-xl border border-white/10 bg-black/20 p-3">
        <div className="mb-3 flex items-start gap-2">
          <Gauge className="mt-0.5 size-4 text-white/70" />
          <div>
            <h4 className="text-sm font-semibold text-white">Speed</h4>
          </div>
        </div>
        <QuickSlider
          label="Speed"
          value={fractalSettings.motionSpeed}
          onPreview={(value) => onFractalChange(normalizeFractalSettings({ ...fractalSettings, motionSpeed: value }))}
          onCommit={(value) => onFractalChange(normalizeFractalSettings({ ...fractalSettings, motionSpeed: value }))}
        />
      </section>
      ) : (
      <section className="rounded-xl border border-white/10 bg-black/20 p-3">
        <div className="mb-3 flex items-start gap-2">
          <Gauge className="mt-0.5 size-4 text-white/70" />
          <div>
            <h4 className="text-sm font-semibold text-white">Feel</h4>
            <p className="mt-1 text-xs text-white/58">Primary controls for the scene. Advanced details stay below.</p>
          </div>
        </div>
        <div className="grid gap-3">
          <QuickSlider
            label="Energy"
            value={quickValues.energy}
            onPreview={(value) => previewQuickControl("energy", value)}
            onCommit={(value) => applyQuickControl("energy", value)}
          />
          <QuickSlider
            label="Softness"
            value={quickValues.softness}
            onPreview={(value) => previewQuickControl("softness", value)}
            onCommit={(value) => applyQuickControl("softness", value)}
          />
          <QuickSlider
            label="Motion"
            value={quickValues.motion}
            onPreview={(value) => previewQuickControl("motion", value)}
            onCommit={(value) => applyQuickControl("motion", value)}
          />
          <QuickSlider
            label="Glow"
            value={quickValues.glow}
            onPreview={(value) => previewQuickControl("glow", value)}
            onCommit={(value) => applyQuickControl("glow", value)}
          />
          <QuickSlider
            label="Depth"
            value={quickValues.depth}
            onPreview={(value) => previewQuickControl("depth", value)}
            onCommit={(value) => applyQuickControl("depth", value)}
          />
        </div>
      </section>
      )}

      {isFractalMode && fractalSettings.fractalVariant === "voxelTunnel" ? (
      <section className="rounded-xl border border-white/10 bg-black/20 p-3">
        <div className="mb-3">
          <h4 className="text-sm font-semibold text-white">Tunnel material</h4>
        </div>
        <div className="grid gap-3">
          <label className="block">
            <div className="mb-1.5 flex items-center justify-between gap-2 text-xs font-medium text-white/82">
              <span>Type</span>
              <span className="text-white/48">
                {VOXEL_MATERIAL_OPTIONS.find((option) => option.value === fractalSettings.voxelMaterialType)?.label ?? "Metal"}
              </span>
            </div>
            <select
              value={fractalSettings.voxelMaterialType}
              onChange={(event) =>
                onFractalChange(
                  normalizeFractalSettings({
                    ...fractalSettings,
                    voxelMaterialType: Number(event.target.value),
                  })
                )
              }
              className="h-9 w-full cursor-pointer rounded-md border border-white/12 bg-white/[0.08] px-3 text-sm font-medium text-white outline-none transition hover:bg-white/[0.12] focus:border-white/25 focus:ring-2 focus:ring-white/15"
            >
              {VOXEL_MATERIAL_OPTIONS.map((option) => (
                <option key={option.value} value={option.value} className="bg-neutral-900 text-white">
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <QuickSlider
            label="Roughness"
            value={fractalSettings.voxelRoughness}
            onPreview={(value) => onFractalChange(normalizeFractalSettings({ ...fractalSettings, voxelRoughness: value }))}
            onCommit={(value) => onFractalChange(normalizeFractalSettings({ ...fractalSettings, voxelRoughness: value }))}
          />
        </div>
      </section>
      ) : null}

      {isFractalMode ? (
      <section className="rounded-xl border border-white/10 bg-black/20 p-3">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-white">Colors</h4>
          <div className="flex shrink-0 gap-1.5">
            <Button
              type="button"
              variant="outline"
              className="h-11 cursor-pointer border-white/15 bg-white/[0.05] px-2 text-[11px] text-white hover:bg-white/[0.1]"
              onClick={handleRandomFractalColors}
            >
              Random
            </Button>
            {token ? (
              <Button
                type="button"
                variant="outline"
                className="h-7 cursor-pointer border-white/15 bg-white/[0.05] px-2 text-[11px] text-white hover:bg-white/[0.1] disabled:cursor-not-allowed disabled:opacity-45"
                onClick={handleSpotifyColors}
                disabled={!dominantColors || dominantColors.length === 0}
                title={dominantColors && dominantColors.length > 0 ? "Use current track colors" : "No track colors yet"}
              >
                Spotify
              </Button>
            ) : null}
          </div>
        </div>
        {fractalSettings.fractalVariant === "infiniteZoom" ? (
          <div className="mb-3 flex flex-wrap gap-2">
            {FRACTAL_PALETTES.slice(0, 3).map((palette) => (
              <Button
                key={palette.id}
                type="button"
                variant="outline"
                className="min-h-11 cursor-pointer border-white/15 px-3 text-xs"
                aria-pressed={palette.colors.every((color, index) => color === fractalSettings.colors[index])}
                onClick={() => {
                  setActivePresetId(null);
                  onFractalChange(normalizeFractalSettings({ ...fractalSettings, colors: [...palette.colors] }));
                }}
              >
                {palette.name}
              </Button>
            ))}
          </div>
        ) : null}
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-3 gap-2">
            {fractalSettings.colors.map((color, index) => (
              <label key={index} className="flex flex-col gap-1 text-[10px] text-white/65">
                {fractalSettings.fractalVariant === "infiniteZoom" ? FRACTAL_COLOR_ROLES[index] : `Color ${index + 1}`}
                <input
                  type="color"
                  value={color}
                  aria-label={fractalSettings.fractalVariant === "infiniteZoom" ? FRACTAL_COLOR_ROLES[index] : `Color ${index + 1}`}
                  title={fractalSettings.fractalVariant === "infiniteZoom" ? FRACTAL_COLOR_ROLES[index] : `Color ${index + 1}`}
                  className="h-11 w-full cursor-pointer rounded border border-white/10 bg-transparent"
                  onChange={(event) => {
                    const colors = [...fractalSettings.colors];
                    colors[index] = event.target.value;
                    onFractalChange(normalizeFractalSettings({ ...fractalSettings, colors }));
                  }}
                />
              </label>
            ))}
          </div>
        </div>
      </section>
      ) : null}

      {!isFractalMode ? (
      <section className="rounded-xl border border-white/10 bg-black/20 p-3">
        <div className="mb-3">
          <h4 className="text-sm font-semibold text-white">Performance</h4>
          <p className="mt-1 text-xs text-white/58">Controls render density without changing your saved scene.</p>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {PERFORMANCE_TIERS.map((tier) => (
            <Button
              key={tier.id}
              type="button"
              variant={performanceTier === tier.id ? "default" : "outline"}
              className="h-auto cursor-pointer flex-col gap-1 px-2 py-2 text-center"
              title={tier.description}
              onClick={() => onPerformanceTierChange(tier.id)}
            >
              <span className="text-xs">{tier.label}</span>
            </Button>
          ))}
        </div>
      </section>
      ) : null}

      <section className="rounded-xl border border-white/10 bg-black/20 p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-start gap-2">
            <MonitorUp className="mt-0.5 size-4 shrink-0 text-white/70" />
            <div className="min-w-0">
              <h4 className="text-sm font-semibold text-white">Keep screen awake</h4>
              <p className="mt-1 text-xs text-white/58">
                {screenWakeLockSupported
                  ? screenWakeLockEnabled
                    ? screenWakeLockActive
                      ? "Screen sleep is blocked while this tab stays visible."
                      : "Waiting for browser permission or visible tab focus."
                    : "Prevents phone or desktop sleep during playback."
                  : "Not supported by this browser."}
              </p>
            </div>
          </div>
          <Switch
            checked={screenWakeLockEnabled}
            onCheckedChange={onScreenWakeLockChange}
            disabled={!screenWakeLockSupported}
            aria-label="Keep screen awake"
          />
        </div>
      </section>

      {!isFractalMode ? (
      <section className="rounded-xl border border-white/10 bg-black/20 p-3">
        <details>
          <summary className="cursor-pointer text-sm font-semibold text-white">Advanced settings</summary>
          <div className="mt-4">
            {viewMode === "kaleidoscope" ? (
              <KaleidoscopeSettingsPanel
                settings={kaleidoscopeSettings}
                onChange={(settings) => {
                  setActivePresetId(null);
                  setQuickPreviewValues(null);
                  onKaleidoscopeChange(settings);
                }}
                canApplySpotifyColors={!!track}
                onApplySpotifyColors={handleSpotifyColors}
              />
            ) : (
              <BlisterSettingsPanel
                settings={blisterSettings}
                onChange={(settings) => {
                  setActivePresetId(null);
                  setQuickPreviewValues(null);
                  onBlisterChange(settings);
                }}
                canApplySpotifyColors={!!track}
                onApplySpotifyColors={handleSpotifyColors}
              />
            )}
          </div>
        </details>
      </section>
      ) : null}

      {!fractalPresetOnly ? (
      <SpotifyPanel
        token={token}
        login={login}
        logout={logout}
        track={track}
        uiVisible={uiVisible}
      />
      ) : null}

      {!isFractalMode && onApplyShareHash ? (
        <section className="rounded-xl border border-white/10 bg-black/20 p-3">
          <div className="mb-3">
            <h4 className="text-sm font-semibold text-white">Import Shared Settings</h4>
            <p className="mt-1 text-xs text-white/60">
              Paste a copied hash or full link to restore settings inside the app.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Textarea
              value={shareHashInput}
              onChange={(event) => setShareHashInput(event.target.value)}
              placeholder="#blister=... or https://...#blister=..."
              className="min-h-20 max-h-[200px] border-white/12 bg-white/[0.05] text-sm text-white placeholder:text-white/35"
            />
            <Button
              type="button"
              variant="outline"
              className="cursor-pointer self-start border-white/15 bg-white/[0.05] text-white hover:bg-white/[0.1]"
              onClick={handleApplyShareHash}
              disabled={!shareHashInput.trim()}
            >
              Apply shared settings
            </Button>
          </div>
        </section>
      ) : null}
    </div>
  );

  return (
    <>
      {/* Fullscreen button */}
      <Button
        className={`absolute top-2 right-2 z-20 cursor-pointer rounded-full border border-white/10 bg-white/[0.08] shadow-[0_12px_40px_rgba(0,0,0,0.22)] backdrop-blur-xl ${controlsVisible ? "opacity-100" : "opacity-0"
          } items-center justify-center hidden md:flex transition-opacity duration-300`}
        onClick={handleFullscreen}
        title={fullscreen ? "Exit Fullscreen" : "Fullscreen"}
        type="button"
        variant="ghost"
        size="icon"
      >
        <FullscreenIcon className="size-7" />
      </Button>

      {/* Settings panel toggle button */}
      <Button
        ref={buttonRef}
        className={`absolute top-2 left-2 z-20 cursor-pointer rounded-full border border-white/10 bg-white/[0.08] shadow-[0_12px_40px_rgba(0,0,0,0.22)] backdrop-blur-xl ${controlsVisible ? "opacity-100" : "opacity-0"
          } transition-opacity duration-300`}
        title="Settings"
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => setSettingsOpen(!settingsOpen)}
      >
        <SettingsIcon className={`size-7 transition-transform duration-300 ${settingsOpen ? 'rotate-90' : ''}`} />
      </Button>

      {/* Settings Card Panel */}
      <div
        ref={settingsRef}
        className={`pointer-events-auto absolute top-2 left-16 z-50 hidden transition-all duration-500 ease-out md:block ${settingsOpen
          ? 'opacity-100 translate-x-0 scale-100'
          : 'opacity-0 -translate-x-4 scale-95 pointer-events-none'
          } ${controlsVisible ? '' : 'pointer-events-none'}`}
      >
        <Card className="relative max-h-[calc(100vh-1rem)] w-[340px] overflow-hidden shadow-2xl">
          <CardContent className="min-h-0 p-4 pt-0">
            {renderPanelContent("max-h-[calc(100vh-5rem)] overflow-y-auto pr-1")}
          </CardContent>
        </Card>
      </div>

      {isMobileViewport ? (
        <Sheet open={settingsOpen} onOpenChange={setSettingsOpen}>
          <SheetContent
            side="bottom"
            className="max-h-[82svh] rounded-t-2xl border-white/12 bg-black/45 p-0"
          >
            <SheetHeader className="border-b border-white/10 pb-3">
              <SheetTitle className="text-white">Scene controls</SheetTitle>
              <SheetDescription className="text-white/58">
                {fractalPresetOnly ? "Choose a curated fractal preset." : "Presets first, detailed controls when needed."}
              </SheetDescription>
            </SheetHeader>
            <div className="min-h-0 overflow-y-auto px-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
            {renderPanelContent()}
            </div>
          </SheetContent>
        </Sheet>
      ) : null}
    </>
  );
}
